import {
  isValidElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useWindowResize } from "@/hooks/use-window-resize";

import { ColumnProps, DataGridProps } from "./types";

export const DataGrid = <Data,>({
  title,
  adjustable,
  data,
  children,
  description,
}: DataGridProps<Data>) => {
  const windowSize = useWindowResize();
  const tableRef = useRef<HTMLTableElement>(null);
  const [tableMaxWidth, setTableMaxWidth] = useState<number>(0);

  const definition = Array.isArray(children) ? children : [children];
  const columns = definition
    .filter(isValidElement)
    .filter((child) => child.type === DataGrid.Column);

  const rows = columns.map((child) => child.props as ColumnProps<Data>);

  useEffect(() => {
    if (tableRef.current) {
      setTableMaxWidth(tableRef.current.clientWidth);
    }
  }, [windowSize.width]);

  const defaultExtractor = useCallback(
    <Data,>(_: string, value: Data): ReactNode => value as ReactNode,
    [],
  );

  return (
    <Card className="flex flex-col flex-1">
      <CardHeader className="pb-4">
        <CardTitle className="font-semibold">{title}</CardTitle>
        {description && (
          <CardDescription className="text-sm text-muted-foreground">
            {description}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="flex-1 pb-4">
        <Table
          ref={tableRef}
          className={adjustable ? "table-auto" : "table-fixed"}
        >
          <TableHeader>
            <TableRow>{columns}</TableRow>
          </TableHeader>
          <TableBody>
            {Object.entries(data || {}).map(([key, value]) => (
              <TableRow key={`row-${key}`}>
                {rows.map((row, index) => {
                  const extractor = row.extractor ?? defaultExtractor;
                  const cell: ReactNode = extractor(key, value);
                  const isEmpty = `${cell}`.trim() === "" || cell === null;

                  const maxWidth = row.ellipsis
                    ? Math.floor(tableMaxWidth / Object.keys(columns).length)
                    : "initial";

                  const classes = [
                    isEmpty && "text-muted-foreground",
                    row.ellipsis && "truncate",
                    row.wrap && "text-wrap",
                    row.center && "text-center",
                    row.right && "text-right",
                    row.emphasis && "font-medium",
                  ]
                    .filter(Boolean)
                    .join(" ");

                  return (
                    <TableCell
                      className={classes}
                      style={{ maxWidth }}
                      key={`cell-${index}`}
                    >
                      {!isEmpty ? (
                        cell
                      ) : (
                        <span className="text-muted-foreground">
                          {row.fallback || "Unavailable"}
                        </span>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

DataGrid.Column = function Column<Data>(props: ColumnProps<Data>) {
  const classes = [
    "text-muted-foreground",
    props?.center && "text-center",
    props?.right && "text-right",
  ]
    .filter(Boolean)
    .join(" ");

  return <TableHead className={classes}>{props.name}</TableHead>;
};
